import { Link } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { AtsCompanyActiveJobsDocument } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

const UNLIMITED_JOBS = -1

export const ActiveJobsLimitWarning = () => {
  const { company } = useCurrentSession()

  const { featureAvailability } = useFeatureAvailability(Features.ats_active_jobs)
  const { data: companyActiveJobs } = useQuery(AtsCompanyActiveJobsDocument, {
    variables: { companyId: company.id },
  })

  const features = companyActiveJobs?.features.featuresConnection.nodes
  const atsExpiredTrialEnabled =
    features?.find((f) => f?.name === 'ats_expired_trial')?.enabled ?? false
  const planInfo = companyActiveJobs?.ats.companiesConnection.nodes[0]
  const isLegacy = featureAvailability.usageLimit === null

  const legacyUsageLimit = planInfo?.planJobPostingsLimit ?? Infinity
  const legacyUsage = planInfo?.activeJobPostingsCount ?? 0
  const nonLegacyUsageLimit = featureAvailability.usageLimit ?? UNLIMITED_JOBS
  const nonLegacyUsage = featureAvailability.usage ?? 0

  const usageLimit = isLegacy ? legacyUsageLimit : nonLegacyUsageLimit
  const usage = isLegacy ? legacyUsage : nonLegacyUsage

  const atsTrialLimit = planInfo?.atsTrialLimit
  const isAtsTrialEnabled =
    atsTrialLimit != null &&
    new Date(atsTrialLimit) > new Date() &&
    (planInfo?.activeJobPostingsCount ?? 0) >= 1 &&
    !atsExpiredTrialEnabled

  const warningMessage = limitMessage(usageLimit, usage, isAtsTrialEnabled)

  if (!warningMessage) return null

  const handleUpgradeClick = () => {
    window.open(
      '/discover/modules/recruitment?upsell_origin=recruitment-jobs-banner&action=init-self-upsell',
      '_blank'
    )
  }

  return (
    <Callout
      description={warningMessage}
      compact
      link={
        <Link.Touchable onClick={handleUpgradeClick}>
          {i18n.t('ats.jobs.limits.upgrade_plan')}
        </Link.Touchable>
      }
      type="warning"
    />
  )
}

const limitMessage = (usageLimit: number, usage: number, isTrial = false) => {
  if (isTrial) {
    return i18n.t('ats.jobs.limits.upsell.title')
  }

  if (usageLimit === UNLIMITED_JOBS) return null

  const jobPostingsLeft = usageLimit - usage

  if (jobPostingsLeft <= 0) {
    return i18n.t('ats.jobs.limits.limit_reached_with_amount', { amount: usageLimit })
  }
  if (jobPostingsLeft === 1) {
    return i18n.t('ats.jobs.limits.amount_left', { published_jobs: usage, limit: usageLimit })
  }
}
